import { TrackedEvents, trackEvent } from 'analytics';
import type { SampleVariant, SiteMaterialSampleWithBreakdown } from 'api';
import { formatDate } from 'date';
import { useTranslation } from 'i18n';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  DataCard,
  ExpandableIconButton,
  formatNumber,
  type SelectChangeEvent,
  Typography,
  useBreakpoint,
} from 'ui';
import { ImageOutlined, ScaleOutlined } from 'ui/icons';

import { MaterialSampleBreakdown } from '../../../breakdowns';
import { SampleSelect } from '../../../quality-report';

export const WeightSampleAnalysisDataCard = ({
  isLoading,
  samples,
  sampleMaterial,
  hasMedia,
}: {
  isLoading?: boolean;
  samples: readonly SiteMaterialSampleWithBreakdown[];
  sampleMaterial?: SampleVariant;
  hasMedia: boolean;
}) => {
  const { t } = useTranslation('features');
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const handleClickMedia = () => {
    trackEvent(TrackedEvents.QUALITY_REPORT_CLICK_CATEGORY_IMAGES, {
      category: 'Manual Sample Analysis',
    });

    const searchParams = new URLSearchParams();
    searchParams.set('tab', 'sample');
    if (selectedSample) {
      searchParams.set('sampleId', selectedSample.id.toString());
    }

    navigate({
      pathname: 'media',
      search: searchParams.toString(),
    });
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const sampleId = searchParams.get('sampleId');

  const [selectedSample, setSelectedSample] = useState<SiteMaterialSampleWithBreakdown | null>(samples?.[0] ?? null);

  const totalWeightInKg = formatNumber(Number(selectedSample?.total_weight), {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'kilogram',
  });

  const handleChangeSample = (event: SelectChangeEvent<string>) => {
    const selected = samples?.find((sample) => sample?.id === Number(event.target.value));
    if (selected) {
      setSelectedSample(selected);
      setSearchParams({ sampleId: selected.id.toString() }, { replace: true });
    }
  };

  useEffect(() => {
    if (!samples?.length) {
      return;
    }

    if (!sampleId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedSample(samples?.[0]);
      return;
    }

    const selected = samples?.find((sample) => sample?.id === Number(sampleId));
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (selected) setSelectedSample(selected);
  }, [samples, sampleId]);

  return (
    <DataCard
      isLoading={isLoading}
      slotProps={{
        header: {
          action: hasMedia ? (
            <ExpandableIconButton
              icon={<ImageOutlined />}
              label={t('dataCards.weightSampleAnalysis.actions.showMedia')}
              onClick={handleClickMedia}
            />
          ) : undefined,
          avatar: <ScaleOutlined />,
          title: t('dataCards.weightSampleAnalysis.title'),
        },
      }}
    >
      <DataCard.Item colSpan={isMobile ? 12 : 6}>
        <Typography
          variant="body1"
          color="secondary"
          sx={{
            paddingBottom: 2,
          }}
        >
          {selectedSample?.total_weight ? (
            <>
              {t('dataCards.weightSampleAnalysis.description.withTotalWeight', {
                values: { totalWeightInKg },
              })}
            </>
          ) : (
            <>{t('dataCards.weightSampleAnalysis.description.withoutTotalWeight')}</>
          )}
        </Typography>
      </DataCard.Item>
      {samples ? (
        <DataCard.Item
          title={samples.length === 1 ? t('dataCards.weightSampleAnalysis.labels.latestInspection') : ''}
          colSpan={isMobile ? 12 : 6}
        >
          {samples.length === 1 ? (
            formatDate(samples[0].date)
          ) : (
            <SampleSelect selectedSample={selectedSample} handleChangeSample={handleChangeSample} samples={samples} />
          )}
        </DataCard.Item>
      ) : null}
      <Box
        sx={{
          mb: 2,
          width: '100%',
        }}
      >
        <MaterialSampleBreakdown
          items={(selectedSample?.breakdown ?? []).map((item) => ({
            label: item.name_display,
            unit: item.unit,
            unit_display: item.unit_display,
            value: item.value,
          }))}
          variant={sampleMaterial}
          precision={2}
        />
      </Box>
    </DataCard>
  );
};
